/**
 * Our `__vitePreload`, bundled in place of vite's helper for every dynamic
 * import vite rewrites and for vite.preload.ts (see vitePreloadHelpersPlugin).
 * Same contract as vite's, except that:
 * - every caller waits for a dependency, where vite only remembers that it
 *   was requested, so a stylesheet still loading from an earlier call
 *   resolves at once for the next one
 * - a stylesheet link a bootloader inserted is read through the flags its
 *   handlers leave (see vite.bootloader.ts), where vite takes any existing
 *   stylesheet link as applied
 * Left out, as this build never needs them: the non-ES format branch, the
 * `modulepreload` support check (the config keeps vite's polyfill) and the
 * CSP nonce.
 * see: https://github.com/vitejs/vite/blob/de1111ab0be00879b404e7ed3b2a80e264edddc1/packages/vite/src/node/plugins/importAnalysisBuild.ts#L117
 */

import {
  isCss,
  STR_ERROR,
  STR_LINK,
  STR_LOAD,
  STR_MODULEPRELOAD,
  STR_SCRIPT,
  STR_STYLESHEET,
} from "../env/strings";

// the wait on each dependency, by its resolved url
const seen: Record<string, Promise<void> | undefined> = {};

const cssError = (dep: string) => new Error(`Unable to preload CSS for ${dep}`);

const loading = (link: HTMLLinkElement, dep: string) =>
  new Promise<void>((resolve, reject) => {
    link.addEventListener(STR_LOAD, () => resolve());
    link.addEventListener(STR_ERROR, () => reject(cssError(dep)));
  });

// a stylesheet link already in the document: a bootloader's handlers clear
// the one that fired, so both set is pending, `onerror` alone cleared is
// failed; anything else, like a link from the html, is applied
const existing = (link: HTMLLinkElement, dep: string): Promise<void> =>
  !link.onload
    ? Promise.resolve()
    : link.onerror
      ? loading(link, dep)
      : Promise.reject(cssError(dep));

function prepare(dep: string): Promise<void> {
  const css = isCss(dep);
  const links = document.getElementsByTagName(STR_LINK);
  for (let i = links.length; i--; ) {
    const link = links[i] as HTMLLinkElement;
    if (link.href === dep && (!css || link.rel === STR_STYLESHEET)) {
      return css ? existing(link, dep) : Promise.resolve();
    }
  }
  const link = document.createElement(STR_LINK);
  link.rel = css ? STR_STYLESHEET : STR_MODULEPRELOAD;
  if (!css) link.as = STR_SCRIPT;
  link.crossOrigin = "";
  link.href = dep;
  const wait = css ? loading(link, dep) : Promise.resolve();
  document.head.appendChild(link);
  return wait;
}

// vite resolves the dependency under the base, then as a module specifier
const resolve = (dep: string) =>
  new URL(import.meta.env.BASE_URL + dep, import.meta.url).href;

function handlePreloadError(reason: unknown): void {
  const event = new Event("vite:preloadError", { cancelable: true });
  (event as Event & { payload: unknown }).payload = reason;
  dispatchEvent(event);
  if (!event.defaultPrevented) throw reason;
}

export function __vitePreload<TModule>(
  baseModule: () => Promise<TModule>,
  deps?: string[],
  // the caller's import.meta.url, for a relative base this build doesn't use
  _importerUrl?: string
): Promise<TModule> {
  const waits = (deps ?? []).map(
    (dep) => ((dep = resolve(dep)), (seen[dep] ??= prepare(dep)))
  );
  return Promise.all(
    waits.map((wait) =>
      wait.then(
        () => null,
        (reason: unknown) => ({ reason })
      )
    )
  ).then((results) => {
    for (const rejected of results) {
      if (rejected) handlePreloadError(rejected.reason);
    }
    return baseModule().catch(handlePreloadError) as Promise<TModule>;
  });
}
